/**
 * Quick-add parser — spec §5-F4.
 *
 * Shared by the server (`POST /tasks/quick-add`) and the client (live token
 * highlighting), so both agree token-for-token.
 *
 *   `Fix login crash on Android @paras #VL !p0 ~bug tomorrow +3h`
 *
 * Date maths uses the **UTC calendar components** of `now`. Callers that care
 * about a user's timezone pass a `now` already shifted into that zone (the
 * server's `zonedNow()` helper does exactly that), so the parser itself stays
 * timezone-free and deterministic.
 */
import { PRIORITIES, TASK_TYPES } from './enums.js';
const WEEKDAYS = {
    sun: 0, sunday: 0,
    mon: 1, monday: 1,
    tue: 2, tues: 2, tuesday: 2,
    wed: 3, weds: 3, wednesday: 3,
    thu: 4, thur: 4, thurs: 4, thursday: 4,
    fri: 5, friday: 5,
    sat: 6, saturday: 6,
};
const MONTHS = {
    jan: 0, january: 0,
    feb: 1, february: 1,
    mar: 2, march: 2,
    apr: 3, april: 3,
    may: 4,
    jun: 5, june: 5,
    jul: 6, july: 6,
    aug: 7, august: 7,
    sep: 8, sept: 8, september: 8,
    oct: 9, october: 9,
    nov: 10, november: 10,
    dec: 11, december: 11,
};
const PRIORITY_ALIASES = {
    p0: 'P0', critical: 'P0', urgent: 'P0',
    p1: 'P1', high: 'P1',
    p2: 'P2', medium: 'P2', med: 'P2', normal: 'P2',
    p3: 'P3', low: 'P3',
};
const MONTH_NAMES = Object.keys(MONTHS).sort((a, b) => b.length - a.length).join('|');
const WEEKDAY_NAMES = Object.keys(WEEKDAYS).sort((a, b) => b.length - a.length).join('|');
function pad(n) {
    return n < 10 ? `0${n}` : String(n);
}
function toISODate(d) {
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
function startOfUTCDay(now) {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
}
function addDays(d, days) {
    const next = new Date(d.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
}
/** Nearest date >= today whose weekday matches. */
function nextWeekday(today, weekday) {
    const delta = (weekday - today.getUTCDay() + 7) % 7;
    return addDays(today, delta);
}
/** Matches later in the list only win if they do not overlap an earlier one. */
function collect(input, re, kind, resolve, out) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(input)) !== null) {
        const value = resolve(m);
        if (value === undefined)
            continue;
        const start = m.index;
        const end = m.index + m[0].length;
        if (out.some((o) => start < o.end && end > o.start))
            continue;
        out.push({ kind, start, end, raw: m[0], value });
    }
}
export function parseQuickAdd(input, now = new Date()) {
    const today = startOfUTCDay(now);
    const matches = [];
    // Explicit sigil tokens first — they are unambiguous.
    collect(input, /(^|\s)@([A-Za-z][\w.\-']{0,39})/g, 'assignee', (m) => m[2].toLowerCase(), matches);
    collect(input, /(^|\s)#([A-Za-z][A-Za-z0-9]{1,4})(?![\w-])/g, 'project', (m) => m[2].toUpperCase(), matches);
    collect(input, /(^|\s)!([A-Za-z0-9]+)/g, 'priority', (m) => PRIORITY_ALIASES[m[2].toLowerCase()], matches);
    collect(input, /(^|\s)~([A-Za-z]+)/g, 'type', (m) => TASK_TYPES.includes(m[2].toLowerCase()) ? m[2].toLowerCase() : undefined, matches);
    // Estimate: +2h, +90m, +1.5h, +2h30m
    collect(input, /(^|\s)\+(\d+(?:\.\d+)?)\s*h(?:rs?|ours?)?(?:\s*(\d+)\s*m(?:in(?:s|utes?)?)?)?\b/gi, 'estimate', (m) => String(Math.round(parseFloat(m[2]) * 60) + (m[3] ? parseInt(m[3], 10) : 0)), matches);
    collect(input, /(^|\s)\+(\d+)\s*m(?:in(?:s|utes?)?)?\b/gi, 'estimate', (m) => String(parseInt(m[2], 10)), matches);
    collect(input, /(^|\s)\+(\d+)\s*d(?:ays?)?\b/gi, 'estimate', (m) => String(parseInt(m[2], 10) * 480), matches);
    // Dates — longest / most specific phrasings first.
    collect(input, /(^|\s)in\s+(\d{1,3})\s+(day|days|week|weeks|month|months)\b/gi, 'due', (m) => {
        const n = parseInt(m[2], 10);
        const unit = m[3].toLowerCase();
        if (unit.startsWith('day'))
            return toISODate(addDays(today, n));
        if (unit.startsWith('week'))
            return toISODate(addDays(today, n * 7));
        const d = new Date(today.getTime());
        d.setUTCMonth(d.getUTCMonth() + n);
        return toISODate(d);
    }, matches);
    collect(input, new RegExp(`(^|\\s)next\\s+(${WEEKDAY_NAMES})\\b`, 'gi'), 'due', (m) => toISODate(addDays(nextWeekday(today, WEEKDAYS[m[2].toLowerCase()]), 7)), matches);
    collect(input, /(^|\s)next\s+week\b/gi, 'due', () => toISODate(addDays(today, 7)), matches);
    collect(input, /(^|\s)(\d{4})-(\d{2})-(\d{2})\b/g, 'due', (m) => {
        const d = new Date(Date.UTC(+m[2], +m[3] - 1, +m[4]));
        return Number.isNaN(d.getTime()) ? undefined : toISODate(d);
    }, matches);
    // `25 aug` / `25 aug 2026`
    collect(input, new RegExp(`(^|\\s)(\\d{1,2})\\s*(?:st|nd|rd|th)?\\s+(${MONTH_NAMES})\\b(?:\\s+(\\d{4}))?`, 'gi'), 'due', (m) => resolveDayMonth(today, parseInt(m[2], 10), MONTHS[m[3].toLowerCase()], m[4]), matches);
    // `aug 25`
    collect(input, new RegExp(`(^|\\s)(${MONTH_NAMES})\\s+(\\d{1,2})\\b(?:,?\\s+(\\d{4}))?`, 'gi'), 'due', (m) => resolveDayMonth(today, parseInt(m[3], 10), MONTHS[m[2].toLowerCase()], m[4]), matches);
    collect(input, /(^|\s)(today|tod)\b/gi, 'due', () => toISODate(today), matches);
    collect(input, /(^|\s)(tomorrow|tmrw?|tmr)\b/gi, 'due', () => toISODate(addDays(today, 1)), matches);
    collect(input, new RegExp(`(^|\\s)(?:this\\s+)?(${WEEKDAY_NAMES})\\b`, 'gi'), 'due', (m) => toISODate(nextWeekday(today, WEEKDAYS[m[2].toLowerCase()])), matches);
    matches.sort((a, b) => a.start - b.start);
    // Strip consumed spans out of the title.
    let title = '';
    let cursor = 0;
    for (const m of matches) {
        title += input.slice(cursor, m.start);
        // keep the leading separator the regex may have eaten
        if (/^\s/.test(m.raw))
            title += ' ';
        cursor = m.end;
    }
    title += input.slice(cursor);
    title = title.replace(/\s+/g, ' ').trim();
    const tokens = matches.map((m) => ({
        kind: m.kind,
        raw: m.raw.trimStart(),
        start: m.start + (m.raw.length - m.raw.trimStart().length),
        end: m.end,
        value: m.value,
    }));
    const first = (kind) => matches.find((m) => m.kind === kind)?.value;
    const estimateRaw = first('estimate');
    const priorityRaw = first('priority');
    const typeRaw = first('type');
    return {
        title,
        projectKey: first('project'),
        assigneeTokens: matches.filter((m) => m.kind === 'assignee').map((m) => m.value),
        priority: priorityRaw && PRIORITIES.includes(priorityRaw)
            ? priorityRaw
            : undefined,
        type: typeRaw,
        dueDate: first('due'),
        estimateMinutes: estimateRaw ? parseInt(estimateRaw, 10) : undefined,
        tokens,
    };
}
/** A bare `25 aug` means the next such date, rolling into next year if past. */
function resolveDayMonth(today, day, month, yearRaw) {
    if (day < 1 || day > 31)
        return undefined;
    const year = yearRaw ? parseInt(yearRaw, 10) : today.getUTCFullYear();
    let d = new Date(Date.UTC(year, month, day));
    if (d.getUTCMonth() !== month)
        return undefined; // e.g. 31 feb
    if (!yearRaw && d < today) {
        d = new Date(Date.UTC(year + 1, month, day));
    }
    return toISODate(d);
}
//# sourceMappingURL=quickAdd.js.map